import { ExecuteQueryByWidgetId } from "@sisense/sdk-ui";
import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { InsightsTableData } from "./InsightsWidgetSelectionDialog";
import { DashboardModel, FilterDimension } from "./types";
import { useGlobalSnackbar } from "../../consecutive-snackbar/GlobalSnackbar";
import {
  Filter,
  filters,
  createDimension,
  createAttribute,
} from "@sisense/sdk-data";

/** @todo Support more filter types as Sisense expands CSDK filtering */
const getFilterType = (type: string): string => {
  switch (type) {
    case "numeric":
      return "numeric-attribute";
    case "text":
    default:
      return "text-attribute";
  }
};

export interface ExecuteMultiQueryByWidgetIdsProps {
  widgetOids: string[];
  dashboardOid: string;
  selectedDashboard: string;
  sisenseEnv: string;
  appContext: any;
  confirmSelections: (tableData?: InsightsTableData[]) => void;
}

export const ExecuteMultiQueryByWidgetIds: FunctionComponent<
  ExecuteMultiQueryByWidgetIdsProps
> = ({
  widgetOids,
  dashboardOid,
  sisenseEnv,
  appContext,
  selectedDashboard,
  confirmSelections,
}) => {
  const [tableData, setTableData] = useState<InsightsTableData[]>([]);
  const [widgetExecutedStates, setWidgetExecutedStates] = useState(
    new Map(widgetOids.map((widgetOid: any) => [widgetOid, false]))
  );
  const [dashboard, setDashboard] = useState<DashboardModel | undefined>();
  const [isLoadingDashboard, setIsLoadingDashboard] = useState<boolean>();
  const [dashboardError, setDashboardError] = useState<Error | undefined>(
    undefined
  );

  const { simple } = useGlobalSnackbar();

  useEffect(() => {
    setIsLoadingDashboard(true);
    appContext.$$http
      .get(`${sisenseEnv}/api/v1/dashboards/${selectedDashboard}`)
      .then((response: any) => {
        setDashboard(response.data);
        setIsLoadingDashboard(false);
      })
      .catch((error: Error) => {
        setDashboardError(error);
        setIsLoadingDashboard(false);
      });
  }, []);

  const addFiltersToQuery = (filterJaqls: any[], qFilters: Filter[]) => {
    filterJaqls.forEach((filterJaql: any) => {
      const dim = filterJaql.dim;
      const type = filterJaql.datatype;
      const columnName = filterJaql.column.replace(/\s/g, "");
      const filterDimension = createDimension({
        name: "Dimension",
        FilterDim: createAttribute({
          name: columnName,
          type: getFilterType(type),
          expression: dim,
        }),
      }) as FilterDimension;
      const member = filters.members(
        filterDimension[columnName],
        filterJaql.filter.members
      );
      qFilters.push(member);
    });
  };

  const queryFilters = useMemo(() => {
    if (!dashboard) return [];
    if (dashboard.filters.length === 0) return [];

    const qFilters: Filter[] = [];
    dashboard.filters.forEach((filter: any) => {
      const filterJaqls = [];
      if (filter.jaql) {
        const jaql = filter.jaql;
        if (jaql.filter && jaql.filter.members) {
          filterJaqls.push(jaql);
        }
      } else if (filter.levels) {
        filter.levels.forEach((level: any) => {
          if (level.filter.members) {
            filterJaqls.push(level);
          }
        });
      }

      addFiltersToQuery(filterJaqls, qFilters);
    });

    return qFilters;
  }, [dashboard]);

  useEffect(() => {
    if (!dashboardError) return;
    simple(
      `Failed loading insights dashboard. Reason: ${dashboardError.message}`,
      { severity: "error" }
    );
  }, [dashboardError, simple]);

  useEffect(() => {
    if (!widgetOids.length) {
      confirmSelections();
    } else if (tableData.length === widgetOids.length) {
      confirmSelections(tableData);
    }
  }, [confirmSelections, tableData, widgetOids.length]);

  return (
    <div>
      {!isLoadingDashboard &&
        widgetOids.length &&
        widgetOids.map((w, index) => {
          if (!widgetExecutedStates.get(w)) {
            return (
              <ExecuteQueryByWidgetId
                key={index}
                widgetOid={w}
                dashboardOid={dashboardOid}
                filters={queryFilters}
                filtersMergeStrategy="codeFirst"
              >
                {(data: any) => {
                  if (data) {
                    const insightsTableData: InsightsTableData = {
                      widgetId: w,
                      columns: data.columns,
                      rows: data.rows,
                    };
                    setTableData([...tableData, insightsTableData]);
                    setWidgetExecutedStates(widgetExecutedStates.set(w, true));
                    return <></>;
                  }

                  return <></>;
                }}
              </ExecuteQueryByWidgetId>
            );
          }
        })}
    </div>
  );
};
